// src/pages/appointments/AppointmentsFilterSheet.jsx
import React from "react";
import { useLocation } from "react-router-dom";
import Sheet from "@/sot/primitives/Sheet.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import FullBleedDivider from "@/sot/primitives/FullBleedDivider";
import DatePicker from "@/sot/primitives/datePicker";
import { parseISO, format as fmt } from "date-fns";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

const ICONS = {
  back: "/icons/filter-doctors/back-arrow.svg",
  cancel: "/icons/doctor/cancel.svg",
  star: "/icons/filter-doctors/star.svg",
  distance: "/icons/filter-doctors/distance.svg",
  verified: "/icons/patient/verified_filled.svg",
  calendar: "/icons/doctor/calendar_clock.svg",
  language: "/icons/filter-doctors/language.svg",
  specialty: "/icons/services/doctor.svg",
  search: "/icons/doctor/search.svg",
};

function toggleInArray(arr, id) {
  if (!Array.isArray(arr)) return [id];
  return arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];
}

/** Row divider for list cards:
 * - inset LEFT only (under icon column)
 * - NEVER inset RIGHT
 * - always reaches the card edge
 */
function RowDivider() {
  const left = "var(--rail-pad)";
  return (
    <div
      className="h-px bg-[rgba(91,107,134,0.20)]"
      style={{
        marginLeft: left,
        width: `calc(100% - ${left})`,
      }}
    />
  );
}

function CheckMark({ on }) {
  return (
    <span
      className={[
        "ml-3 shrink-0 h-6 w-6 rounded-[6px] flex items-center justify-center",
        on
          ? "bg-[rgb(var(--afrimed-primary))] text-[rgb(var(--afrimed-on-primary))]"
          : "border border-[rgba(var(--clr-outline-variant),0.95)] bg-[rgba(var(--clr-surface-container-highest),0.92)] text-transparent",
      ].join(" ")}
      aria-hidden
    >
      {on ? "✓" : ""}
    </span>
  );
}

// --- CSV helpers (more robust) ---
function splitCsvLine(line) {
  const out = [];
  let cur = "";
  let q = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') q = !q;
    else if (ch === "," && !q) {
      out.push(cur);
      cur = "";
    } else cur += ch;
  }
  out.push(cur);
  return out.map((s) => s.trim().replace(/^"|"$/g, ""));
}

function titleCaseSmart(s = "") {
  let t = String(s).trim();
  if (!t) return "";
  t = t.replace(/\d+$/g, "").trim();
  if (!t) return "";
  if (/^[A-Z]{2,4}$/.test(t)) return t;

  return t
    .toLowerCase()
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ")
    .replace(/\bSetswana\b/i, "Setswana");
}

function chooseBestColumn(headers, preferredKeys) {
  const bad = (h) =>
    h.includes("code") ||
    h.includes("iso") ||
    h.includes("count") ||
    h.includes("freq") ||
    h.includes("weight") ||
    h.includes("rank");

  const strong = headers.findIndex(
    (h) =>
      !bad(h) &&
      (h.includes("full") || h.includes("name")) &&
      (h.includes("lang") || h.includes("language"))
  );
  if (strong >= 0) return strong;

  for (const key of preferredKeys) {
    const idx = headers.findIndex((h) => !bad(h) && h.includes(key));
    if (idx >= 0) return idx;
  }

  const firstGood = headers.findIndex((h) => !bad(h));
  if (firstGood >= 0) return firstGood;

  return 0;
}

async function loadCsvColumn(path, preferredKeys = ["language", "lang", "name"]) {
  const res = await fetch(path, { cache: "no-store" });
  if (!res.ok) return [];
  const text = await res.text();
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (!lines.length) return [];

  const header = splitCsvLine(lines[0]).map((h) => h.toLowerCase());
  const useIdx = chooseBestColumn(header, preferredKeys);

  return lines
    .slice(1)
    .map((l) => splitCsvLine(l)[useIdx])
    .map(titleCaseSmart)
    .filter(Boolean);
}

const LANGUAGE_CSV_PATHS = [
  "/data/language_use_angola.csv",
  "/data/language_use_botswana.csv",
  "/data/language_use_mozambique.csv",
  "/data/language_use_namibia.csv",
  "/data/language_use_southafrica.csv",
  "/data/language_use_zambia.csv",
  "/data/language_use_zimbabwe.csv",
];

function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatDMY(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
}

function FullBleedLine({ className = "" }) {
  return (
    <div className={["mt-4 -mx-4 sm:-mx-6", className].join(" ")}>
      <FullBleedDivider />
    </div>
  );
}

function selectedLabel(list, emptyLabel = "Any", maxItems = 3) {
  const arr = Array.isArray(list)
    ? list.map(String).map((s) => s.trim()).filter(Boolean)
    : [];
  if (!arr.length) return emptyLabel;

  const shown = arr.slice(0, maxItems);
  const rest = arr.length - shown.length;
  return rest > 0 ? `${shown.join(", ")} +${rest}` : shown.join(", ");
}

const FRENCH_FILTER_LABELS = {
  specialty: {
    Dermatologist: "Dermatologue",
    Dermatology: "Dermatologie",
    "General Practice": "Médecine générale",
    "Allergy/Immunology": "Allergologie / Immunologie",
    "Preventive Medicine": "Médecine préventive",
    "Emergency Medicine": "Médecine d’urgence",
    Pediatric: "Pédiatrie",
    Paediatrician: "Pédiatre",
    Cardiologist: "Cardiologue",
    Endocrinologist: "Endocrinologue",
  },
  language: {
    English: "Anglais",
    French: "Français",
    Tswana: "Setswana",
    Setswana: "Setswana",
    "Zimbabwean Ndebele": "Ndebele du Zimbabwe",
    Portuguese: "Portugais",
    German: "Allemand",
  },
};

function isFrenchLanguageCode(languageCode = "") {
  return String(languageCode || "").trim().toLowerCase().startsWith("fr");
}

function localizeFilterLabel(value, languageCode, group) {
  const raw = String(value || "").trim();
  if (!raw || !isFrenchLanguageCode(languageCode)) return raw;
  return FRENCH_FILTER_LABELS[group]?.[raw] || raw;
}

function hasFrenchFilterLabel(value, group) {
  const raw = String(value || "").trim();
  return Boolean(raw && FRENCH_FILTER_LABELS[group]?.[raw]);
}

function selectedDisplayLabel(list, emptyLabel, maxItems, languageCode, group) {
  const arr = Array.isArray(list)
    ? list.map(String).map((s) => s.trim()).filter(Boolean)
    : [];
  if (!arr.length) return emptyLabel;
  const shown = arr
    .slice(0, maxItems)
    .map((item) => localizeFilterLabel(item, languageCode, group));
  const rest = arr.length - shown.length;
  return rest > 0 ? `${shown.join(", ")} +${rest}` : shown.join(", ");
}

/** Blueprint ghost search row (search icon left, cancel right)
 * - Padding controlled by rail-pad
 * - NO divider inside (parent places FullBleedLine)
 */
function GhostSearchRow({ value, onChange, placeholder, onCancel, clearLabel = "Clear search" }) {
  const has = !!String(value || "").trim();

  return (
    <div className="mt-2">
      <div className="px-[var(--rail-pad)] flex items-center gap-3">
        <img
          src={ICONS.search}
          alt=""
          className="h-5 w-5 opacity-70 brightness-0 dark:invert"
          draggable="false"
        />

        <input
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          inputMode="search"
          autoCapitalize="none"
          autoCorrect="off"
          autoComplete="off"
          className="
            flex-1 bg-transparent outline-none border-0
            text-[16px] leading-6
            placeholder:text-on-surface-var text-on-surface
          "
        />

        {has ? (
          <button
            type="button"
            onClick={onCancel}
            className="h-9 w-9 rounded-full grid place-items-center"
            aria-label={clearLabel}
          >
            <img
              src={ICONS.cancel}
              alt=""
              className="h-5 w-5 opacity-70"
              draggable="false"
            />
          </button>
        ) : null}
      </div>
    </div>
  );
}

let _cachedSpecialties = null;
let _cachedLanguages = null;

export default function AppointmentsFilterSheet({
  open,
  mode,
  value,
  onChange,
  onClose,
  onOpenMode,
}) {
  const location = useLocation();
  const { t, languageCode } = useLanguage();

  const isFilterOpen = !!open;
  const filterMode = mode || "main";

  // 🔒 keep sheet ONLY on appointments browse surfaces
  React.useEffect(() => {
    if (!isFilterOpen) return;

    const allowed = new Set([
      "/appointments",
      "/appointments/doctors",
      "/appointments/hospitals",
      "/appointments/pharmacies",
      "/appointments/nearby",
    ]);

    if (!allowed.has(location.pathname)) {
      onClose?.();
    }
  }, [isFilterOpen, location.pathname, onClose]);

  const filters =
    value || { data: [], specialty: [], language: [], payment: [], date: null };

  const setFilters = onChange;
  const closeFilters = onClose;

  const [specQuery, setSpecQuery] = React.useState("");
  const [langQuery, setLangQuery] = React.useState("");
  const [specialties, setSpecialties] = React.useState([]);
  const [languages, setLanguages] = React.useState([]);
  const [loadingSpec, setLoadingSpec] = React.useState(false);
  const [loadingLang, setLoadingLang] = React.useState(false);

  React.useEffect(() => {
  let alive = true;

  if (_cachedSpecialties) {
    setSpecialties(_cachedSpecialties);
    return;
  }

  (async () => {
    setLoadingSpec(true);
    try {
      const list = await loadCsvColumn("/data/clean/specialities.csv", [
        "specialty_name",
        "specialty",
        "name",
        "special",
      ]);
      if (!alive) return;
      _cachedSpecialties = list;
      setSpecialties(list);
    } finally {
      if (alive) setLoadingSpec(false);
    }
  })();

  return () => {
    alive = false;
  };
}, []);

  React.useEffect(() => {
  let alive = true;

  if (_cachedLanguages) {
    setLanguages(_cachedLanguages);
    return;
  }

  (async () => {
    setLoadingLang(true);
    try {
      const all = [];
      for (const p of LANGUAGE_CSV_PATHS) {
        const list = await loadCsvColumn(p, [
          "language_name",
          "full",
          "name",
          "language",
          "lang",
        ]);
        all.push(...list);
      }

      const uniq = Array.from(new Set(all.map((x) => x.trim()).filter(Boolean)))
        .sort((a, b) => a.localeCompare(b));

      if (!alive) return;
      _cachedLanguages = uniq;
      setLanguages(uniq);
    } finally {
      if (alive) setLoadingLang(false);
    }
  })();

  return () => {
    alive = false;
  };
}, []);

  const isMain = filterMode === "main";
  const isSpec = filterMode === "specialty";
  const isLang = filterMode === "language";
  const isDate = filterMode === "date";

  // -----------------------------
  // MAIN (Blueprint)
  // - pills carousel
  // - continuous list + dividers
  // - NO Apply CTA
  // -----------------------------
  const mainContent = (
    <div className="pb-4">
      <div className="mx-2 text-body-medium text-on-surface-var mb-2">
        {t("appointments.directory.sheet.data", "Data")}
      </div>

      {/* horizontal carousel pills */}
      <div className="overflow-x-auto overflow-y-visible [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden py-2 -my-2">
        <div className="flex flex-nowrap gap-2 pr-2 pl-1">    
          {[
            { id: "best-rating", label: t("appointments.directory.sheet.bestRating", "Best rating"), icon: ICONS.star },
            { id: "near-you", label: t("appointments.directory.sheet.nearYou", "Near you"), icon: ICONS.distance },
            { id: "verified", label: t("appointments.directory.sheet.verified", "Verified"), icon: ICONS.verified },
            { id: "evisit", label: t("appointments.directory.common.virtual", "Virtual"), icon: "/icons/filter-doctors/video_call.svg" },
          ].map((it) => {
            const on = filters.data?.includes(it.id);

            return (
              <button
                key={it.id}
                type="button"
                onClick={() =>
                  setFilters((p) => ({ ...p, data: toggleInArray(p.data, it.id) }))
                }
                className={[
                  "h-9 rounded-full px-3 shrink-0",
                  "inline-flex items-center gap-2",
                  "border border-[rgba(91,107,134,0.20)]",
                  "shadow-[0_2px_8px_rgba(15,23,42,0.10)]",
                  on
                    ? "bg-[rgba(24,50,119,0.10)] text-[rgb(var(--afrimed-primary))]"
                    : "bg-[rgb(var(--clr-surface-cont-highest))] text-on-surface",
                ].join(" ")}
              >
                <img src={it.icon} alt="" className="h-4 w-4" draggable="false" />
                <span className="text-body-medium whitespace-nowrap">{it.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* continuous list (no card border wrapper) */}
      <div className="mt-4 rounded-[12px] overflow-hidden bg-[rgb(var(--clr-surface-cont-highest))]">
        <button
          type="button"
          className="w-full flex items-center justify-between h-14 px-[var(--rail-pad)] text-left"
          onClick={() => onOpenMode("specialty")}
        >
          <div className="flex items-center gap-3 min-w-0">
            <img src={ICONS.specialty} alt="" className="h-5 w-5" draggable="false" />
            <span className="text-body-large text-on-surface">
              {t("appointments.directory.sheet.specialty", isFrenchLanguageCode(languageCode) ? "Spécialité" : "Specialty")}
            </span>
          </div>
          <span className="text-body-medium text-on-surface-var">
            {selectedDisplayLabel(filters.specialty, t("appointments.directory.sheet.any", "Any"), 2, languageCode, "specialty")}
          </span>
        </button>

        <RowDivider />

        <button
          type="button"
          className="w-full flex items-center justify-between h-14 px-[var(--rail-pad)] text-left"
          onClick={() => onOpenMode("language")}
        >
          <div className="flex items-center gap-3 min-w-0">
            <img src={ICONS.language} alt="" className="h-5 w-5" draggable="false" />
            <span className="text-body-large text-on-surface">
              {t("appointments.directory.sheet.languages", isFrenchLanguageCode(languageCode) ? "Langues" : "Languages")}
            </span>
          </div>
          <span className="text-body-medium text-on-surface-var">
            {selectedDisplayLabel(filters.language, t("appointments.directory.sheet.any", "Any"), 3, languageCode, "language")}
          </span>
        </button>

        <RowDivider />

        <button
          type="button"
          className="w-full flex items-center justify-between h-14 px-[var(--rail-pad)] text-left"
          onClick={() => onOpenMode("date")}
        >
          <div className="flex items-center gap-3 min-w-0">
            <img src={ICONS.calendar} alt="" className="h-5 w-5" draggable="false" />
            <span className="text-body-large text-on-surface">
              {t("appointments.directory.sheet.availability", isFrenchLanguageCode(languageCode) ? "Disponibilité" : "Availability")}
            </span>
          </div>
          <span className="text-body-medium text-on-surface-var">
            {filters.date ? formatDMY(filters.date) : t("appointments.directory.sheet.any", "Any")}
          </span>
        </button>
      </div>
    </div>
  );

  // -----------------------------
  // SERVICES (Blueprint)
  // TopBarCenter + X (back to main)
  // ghost search row + divider
  // continuous list + dividers
  // -----------------------------
  const specialtyDisplayOptions = isFrenchLanguageCode(languageCode)
    ? specialties.filter((s) => hasFrenchFilterLabel(s, "specialty") || filters.specialty?.includes(s))
    : specialties;
  const specFiltered = specialtyDisplayOptions.filter((s) =>
    localizeFilterLabel(s, languageCode, "specialty")
      .toLowerCase()
      .includes(specQuery.trim().toLowerCase())
  );

  const specialtyContent = (
  <div className="pb-4">
    <GhostSearchRow
      value={specQuery}
      onChange={setSpecQuery}
      placeholder={t("appointments.directory.sheet.searchSpecialty", isFrenchLanguageCode(languageCode) ? "Rechercher une spécialité" : "Search services")}
      clearLabel={t("appointments.directory.sheet.clearSearch", isFrenchLanguageCode(languageCode) ? "Effacer la recherche" : "Clear search")}
      onCancel={() => setSpecQuery("")}
    />

    {/* ✅ the ONLY divider under search, true full-bleed */}
    <div className="mt-4">
      <FullBleedLine />
    </div>

    <div className="mt-6 mx-2 px-[var(--rail-pad)] text-body-large text-on-surface-var">
      {loadingSpec ? t("common.loading", "Loading...") : t("appointments.directory.sheet.specialists", "Specialists")}
    </div>

    <div className="mt-3 px-[var(--rail-pad)]">
      <div className="rounded-[12px] overflow-hidden bg-[rgb(var(--clr-surface-cont-highest))]">
        {(loadingSpec ? [] : specFiltered).slice(0, 200).map((s, idx, arr) => {
          const on = filters.specialty?.includes(s);

          return (
            <React.Fragment key={s}>
              <button
                type="button"
                className="w-full flex items-center justify-between h-14 px-[var(--rail-pad)] text-left"
                onClick={() =>
                  setFilters((p) => ({ ...p, specialty: toggleInArray(p.specialty, s) }))
                }
              >
                <span className="text-body-large text-on-surface">{localizeFilterLabel(s, languageCode, "specialty")}</span>
                <CheckMark on={on} />
              </button>

              {idx !== arr.length - 1 ? <RowDivider variant="plainRow" /> : null}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  </div>
);

// -----------------------------
// LANGUAGES (Blueprint)
// -----------------------------
const languageDisplayOptions = isFrenchLanguageCode(languageCode)
  ? languages.filter((l) => hasFrenchFilterLabel(l, "language") || filters.language?.includes(l))
  : languages;
const langFiltered = languageDisplayOptions.filter((l) =>
  localizeFilterLabel(l, languageCode, "language")
    .toLowerCase()
    .includes(langQuery.trim().toLowerCase())
);

const languageContent = (
  <div className="pb-4">
    <GhostSearchRow
      value={langQuery}
      onChange={setLangQuery}
      placeholder={t("appointments.directory.sheet.searchLanguages", isFrenchLanguageCode(languageCode) ? "Rechercher une langue" : "Search languages")}
      clearLabel={t("appointments.directory.sheet.clearSearch", isFrenchLanguageCode(languageCode) ? "Effacer la recherche" : "Clear search")}
      onCancel={() => setLangQuery("")}
    />

    {/* ✅ match Services: divider under search */}
    <div className="mt-4">
      <FullBleedLine />
    </div>

    <div className="mt-6 px-[var(--rail-pad)] text-body-large text-on-surface-var">
      {loadingLang
        ? t("common.loading", isFrenchLanguageCode(languageCode) ? "Chargement..." : "Loading...")
        : t("appointments.directory.sheet.selectOneOrMore", isFrenchLanguageCode(languageCode) ? "Sélectionnez une ou plusieurs langues" : "Select one or more")}
    </div>

    <div className="mt-3 px-[var(--rail-pad)]">
      <div className="rounded-[12px] overflow-hidden bg-[rgb(var(--clr-surface-cont-highest))]">
        {(loadingLang ? [] : langFiltered).slice(0, 200).map((l, idx, arr) => {
          const on = filters.language?.includes(l);

          return (
            <React.Fragment key={l}>
              <button
                type="button"
                className="w-full flex items-center justify-between h-14 px-[var(--rail-pad)] text-left"
                onClick={() =>
                  setFilters((p) => ({ ...p, language: toggleInArray(p.language, l) }))
                }
              >
                <span className="text-body-large text-on-surface">{localizeFilterLabel(l, languageCode, "language")}</span>
                <CheckMark on={on} />
              </button>

              {idx !== arr.length - 1 ? <RowDivider variant="plainRow" /> : null}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  </div>
);

// -----------------------------
// AVAILABILITY (Blueprint)
// No search (date-driven)
// -----------------------------
const dateContent = (
  <div className="pb-4">
    {/* ✅ TRUE full-bleed divider (matches DoctorPrescriptionWizard pattern) */}
    <FullBleedLine className="mt-2" />

    <div className="mt-6">
      <div className="text-body-medium text-on-surface-var mx-4 mb-2 px-[var(--rail-pad)]">
        {t("appointments.directory.sheet.selectDate", "Select date")}
      </div>

      {/* Date picker surface (AfriMed card) */}
      <div className="px-[var(--rail-pad)]">
        <div className="rounded-[12px] overflow-hidden bg-[rgb(var(--clr-surface-cont-highest))]">
          <DatePicker
            mode="single"
            value={filters.date ? parseISO(filters.date) : null}
            onChange={(d) => {
              // ✅ user stays in control; never auto-close
              setFilters((p) => ({ ...p, date: d ? fmt(d, "yyyy-MM-dd") : null }));
            }}
            onCancel={() => onOpenMode("main")}
            className="!bg-transparent"
            navButtonClassName="bg-[rgba(var(--clr-surface-container-high),0.96)] text-on-surface-var hover:bg-[rgba(var(--clr-on-surface),0.06)]"
          />
        </div>
      </div>
    </div>
  </div>
);

  // -----------------------------
  // HEADERS (Blueprint)
  // - main: Filter + X closes sheet
  // - modes: title + X goes back to main
  // -----------------------------
  const header = (
    <div className="px-4 mb-2">
      {isMain ? (
        <TopBarCenter title={t("appointments.directory.sheet.title", "Filter")} close onBack={closeFilters} margin="mb-0" />
      ) : isSpec ? (
        <TopBarCenter title={t("appointments.directory.sheet.specialty", isFrenchLanguageCode(languageCode) ? "Spécialité" : "Specialty")} close onBack={() => onOpenMode("main")} margin="mb-0" />
      ) : isLang ? (
        <TopBarCenter title={t("appointments.directory.sheet.languages", isFrenchLanguageCode(languageCode) ? "Langues" : "Languages")} close onBack={() => onOpenMode("main")} margin="mb-0" />
      ) : (
        <TopBarCenter title={t("appointments.directory.sheet.availability", isFrenchLanguageCode(languageCode) ? "Disponibilité" : "Availability")} close onBack={() => onOpenMode("main")} margin="mb-0" />
      )}
    </div>
  );

  const body = isSpec
    ? specialtyContent
    : isLang
    ? languageContent
    : isDate
    ? dateContent
    : mainContent;

  if (!isFilterOpen) return null;

  // ✅ IMPORTANT: no inner “sheet shell” container = removes the extra grey card
  return (
    <Sheet open={isFilterOpen} onClose={closeFilters}>
      <div className="flex flex-col max-h-[80vh]">
        {header}

        {/* scrollable content */}
        <div className="flex-1 overflow-y-auto px-[var(--rail-pad)]">
          {body}
        </div>

        {/* ✅ No Apply / No footer (live filters) */}
        <div className="h-4" />
      </div>
    </Sheet>
  );
}
